import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';

import { Job } from './Job';
import { ResolutionDate } from '../telematics/components/Colorizer/ResolutionDate';
import { openModal } from 'svelte-modals';
import JobPhotosModal from './JobPhotosModal.svelte';
import { app } from '../../../stores/app';

type BalloonContentSections = {
    id: boolean | undefined;
    address: boolean | undefined;
    special_attributes: boolean | undefined;
    client_attributes: boolean | undefined;
    service_attributes: boolean | undefined;
};

type BalloonContentGeneratorOptions = {
    cssClasses: boolean | undefined;
    containerId: string | undefined;
    sections: BalloonContentSections | undefined;
};

export class BalloonContentGenerator {
    constructor(private job: Job) {}

    private _capitalize(str: any) {
        if (typeof str !== 'string' || str.length === 0) {
            return '';
        }
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    public getBalloonHtmlArray(
        options: BalloonContentGeneratorOptions | undefined = undefined
    ): string[] {
        options = Object.assign(
            { cssClasses: false, containerId: 'balloon_job_container' + this.job.id, sections: {} },
            options
        );
        options.sections = Object.assign(
            {
                id: true,
                address: true,
                special_attributes: true,
                client_attributes: true,
                service_attributes: true,
            },
            options.sections
        );
        let alreadyUsedKeys: string[] = [];
        let jobData = this.job.getData();
        let html = [];
        if (options.sections.id && this.job.id !== null && this.job.id >= 0) {
            html.push('<div style="opacity:0.5;">#' + this.job.id + '</div>');
        }
        if (options.sections.address && jobData?.computed_address) {
            html.push(
                '<div style="font-size: 14px; font-weight: bold">' +
                    [
                        jobData?.computed_address?.city || '',
                        (jobData?.computed_address?.street || '') +
                            ' ' +
                            (jobData?.computed_address?.house || ''),
                    ]
                        .filter(s => (s || '').trim())
                        .join(', ') +
                    '</div>'
            );

            let line2 = [];
            line2.push(jobData?.address_attributes?.country || 'PL');
            line2.push(jobData?.computed_address?.pcode || '');
            line2.push(jobData?.computed_address?.province || '');
            line2.push(jobData?.computed_address?.community || '');
            html.push(`<div style="margin-bottom: 14px;">
                <div>${line2.filter(w => w !== '').join(', ')}</div>
            </div>`);
            alreadyUsedKeys = [
                'city',
                'street',
                'house',
                'country',
                'pcode',
                'province',
                'community',
            ];
        }

        let sortedAttributes: {
            key: string;
            label: string;
            value: string;
            special: boolean | null | undefined;
        }[] = [];

        let prefferedKeyOrder = [
            'target_date',
            'start_of_time_window',
            'end_of_time_window',
            'earliest_delivery_date',
            'latest_delivery_date',
            'label',
            'delivery_or_collection',
            'parcels',
            'total_weight',
            'fill_percentage',
            'external_id',
        ];

        let attributes = Object.assign(
            {},
            options.sections.client_attributes ? jobData?.client_attributes : {},
            options.sections.special_attributes ? jobData?.special_attributes : {}
        );

        if (0 && options.sections.special_attributes && this.job.getTargetDate() !== null) {
            attributes = Object.assign({}, attributes, { target_date: this.job.getTargetDate() });
        }

        Object.keys(attributes ?? {}).forEach(attr => {
            try {
                if (
                    attributes !== null &&
                    !alreadyUsedKeys.includes(attr) &&
                    attributes[attr] !== null
                ) {
                    let valueStr = attributes[attr].toString() ?? '';
                    if (typeof attributes[attr] === 'boolean') {
                        valueStr = attributes[attr] ? '&check;' : '&cross;';
                    } else if (typeof attributes[attr] === 'object') {
                        if (Array.isArray(attributes[attr])) {
                            valueStr = attributes[attr].join(', ');
                        } else {
                            valueStr = JSON.stringify(attributes[attr]);
                        }
                    }
                    if (
                        attr === 'earliest_delivery_date' ||
                        attr === 'latest_delivery_date' ||
                        attr === 'target_date'
                    ) {
                        let date = new Date(valueStr);
                        if (!isNaN(date.getTime())) {
                            valueStr = ResolutionDate.getShortFormat(date);
                        }
                    }
                    let unit = '';
                    if (attr === 'total_weight') {
                        unit = 'kg';
                    }
                    if (valueStr !== '') {
                        sortedAttributes.push({
                            key: attr,
                            label: unit ? this._capitalize(get(_)(attr)) + ' [' + unit + ']' : this._capitalize(get(_)(attr)),
                            value: get(_)(valueStr.replace(/\xf3/g, 'ó')),
                            special:
                                jobData?.special_attributes && attr in jobData?.special_attributes,
                        });
                        alreadyUsedKeys.push(attr);
                    }
                }
            } catch (e) {
                console.warn(
                    'BalloonContentGenerator, attributes, cannot convert attr',
                    attr,
                    attributes[attr],
                    e
                );
            }
        });

        sortedAttributes = sortedAttributes.sort((a, b) => {
            let aIndex = prefferedKeyOrder.indexOf(a.key);
            let bIndex = prefferedKeyOrder.indexOf(b.key);
            if (aIndex === -1) aIndex = 999;
            if (a.special) aIndex -= 1000;
            if (bIndex === -1) bIndex = 999;
            if (b.special) bIndex -= 1000;
            return aIndex - bIndex;
        });

        // sortedAttributes.sort((a,b) => a.label.localeCompare(b.label));
        let separated = true;
        for (let attr of sortedAttributes) {
            if (attr.special) {
                separated = false;
            }
            // html.push('<div><label style="opacity:0.5;">'+ get(_)(attr) +'</label> '+ get(_)(this.job.getData()?.client_attributes[attr]) +'</div>');
            html.push(
                '<div' +
                    (!separated && !attr.special
                        ? ' style="border-top: 1px solid #ccc; margin-top: 3px; padding-top: 3px"'
                        : '') +
                    '><label style="opacity:0.5;">' +
                    attr.label +
                    ':</label> ' +
                    attr.value +
                    '</div>'
            );
            if (!separated && !attr.special) {
                separated = true;
            }
        }

        if (options.sections.service_attributes) {
            let servicehtml = [];
            let extrahtml = [];
            let deliveredhtml = '';
            let serviceAttributes = jobData?.realisation?.service_attributes || jobData?.rejection?.service_attributes || null;
            if (serviceAttributes) {
                let infoTypes = ['load', 'delivery', 'rejection', 'photo', 'note', 'tags', 'extra'];
                for (let type of infoTypes) {
                    if (serviceAttributes[type]) {
                        switch (type) {
                            case 'load':
                                deliveredhtml =
                                    '<div><label style="opacity:0.5;">' +
                                    get(_)('Job loaded') +
                                    ':</label></div><div style="padding-bottom: 10px; white-space: nowrap;">' +
                                    new Date(
                                        serviceAttributes.load.time * 1000
                                    ).toLocaleString() +
                                    '</div>' + deliveredhtml;
                                break;
                            case 'note':
                                servicehtml.push(
                                    '<div style="' +
                                        (serviceAttributes.photo
                                            ? 'margin-top: 3px'
                                            : '') +
                                        '">' +
                                        serviceAttributes.note +
                                        '</div>'
                                );
                                break;
                            case 'tags':
                                if (serviceAttributes.tags.length) {
                                    servicehtml.push(
                                        '<div style="margin-top: 3px">' +
                                        serviceAttributes.tags
                                                .map(
                                                    (tag: string) =>
                                                        '<span style="display: inline-block; border-radius: 5px; background-color: #ddd; padding: 0px 5px 2px;">'+tag+'</span>'
                                                )
                                                .join('') +
                                            '</div>'
                                    );
                                }
                                break;
                            case 'photo':
                                servicehtml.push(
                                    '<div style="display: flex; flex-direction: row; flex-wrap: wrap;">' +
                                    serviceAttributes.photo
                                            .map(
                                                (p: string) =>
                                                    '<img class="job-photo" style="margin: 3px; cursor: pointer;" src="https://img.targeo.pl/i/cache/' +
                                                    p.replace(/\.(png|jpe?g)$/, '-mini.$1') +
                                                    '"/>'
                                            )
                                            .join('') +
                                        '</div>'
                                );
                                break;
                            case 'delivery':
                                deliveredhtml +=
                                    '<div><label style="opacity:0.5;">' +
                                    get(_)(jobData?.special_attributes?.delivery_or_collection === 'collection' ? 'Job collected' : 'Job delivered') +
                                    ':</label></div><div style="padding-bottom: 10px; white-space: nowrap;">' +
                                    new Date(
                                        serviceAttributes.delivery * 1000
                                    ).toLocaleString() +
                                    '</div>';
                                break;
                            case 'rejection':
                                deliveredhtml +=
                                    '<div><label style="opacity:0.5;">' +
                                    get(_)('Job rejected') +
                                    ':</label></div><div style="padding-bottom: 10px; white-space: nowrap;">' +
                                    new Date(
                                        serviceAttributes.rejection * 1000
                                    ).toLocaleString() +
                                    '</div>';
                                break;
                            case 'extra':
                                if (Object.keys(serviceAttributes.extra).length) {
                                    let extraAttributesLabels = get(app).account.getSettingsReadonly()?.mobile_app_options?.delivery_action_attributes?.[jobData?.special_attributes?.delivery_or_collection || '']
                                    for (let attr in serviceAttributes.extra) {
                                        if (['total_weight', 'parcels', 'non_normative_size'].indexOf(attr) == -1) {
                                            let xlabel = extraAttributesLabels?.[attr]?.label || attr;
                                            let xoption = extraAttributesLabels?.[attr]?.options?.filter((o: any) => typeof(o) == 'object' && Object.keys(o)[0] == serviceAttributes.extra[attr])[0];
                                            extrahtml.push('<div style="padding-top: 3px"><label style="opacity: 0.5;">'+xlabel+':</label></div><div>'+(xoption ? Object.values(xoption)[0] : serviceAttributes.extra[attr])+'</div>');
                                        }
                                    }
                                }
                        }
                    }
                }
            }
            if (servicehtml.length || deliveredhtml || extrahtml.length) {
                html.unshift(
                    '<div style="display: flex; flex-direction: row"><div style="min-width: 60%">'
                );
                html.push(
                    '</div><div style="margin-left: 10px; padding-left: 10px; border-left: 1px solid #c4c4c4">'
                );
                if (deliveredhtml) {
                    html.push(deliveredhtml);
                }
                if (servicehtml.length) {
                    html.push(
                        '<div><label style="opacity:0.5;">' +
                            get(_)('Job drivers info') +
                            ':</label></div>'
                    );
                    html.push(servicehtml.join(''));
                }
                if (extrahtml.length) {
                    html.push(extrahtml.join(''));
                }
                html.push('</div></div>');
            }
        }

        html.unshift(
            '<div id="balloon_job_container_' + this.job.id + '" style="user-select: text">'
        );
        html.push('</div>');

        return html;
    }

    public connectBalloonThumbnails(containerId: string | undefined = undefined) {
        if (!containerId) {
            containerId = 'balloon_job_container_' + this.job.id;
        }
        let jobData = this.job.getData();
        if (jobData) {
            let photoURLs = jobData?.realisation?.service_attributes?.photo || jobData?.rejection?.service_attributes?.photo;
            if (photoURLs && photoURLs.length) {
                photoURLs = photoURLs.map((p: string) => 'https://img.targeo.pl/i/cache/' + p);
                let thumbnails = document.querySelectorAll('#' + containerId + ' .job-photo');
                for (let i = 0; i < thumbnails.length; i++) {
                    thumbnails[i].addEventListener(
                        'click',
                        function(idx: number) {
                            openModal(JobPhotosModal, { photos: photoURLs, current: idx });
                        }.bind(thumbnails[i], i)
                    );
                }
            }
        }
    }
}
